import { useContext, useEffect, useRef, useState } from "react";
import { FEATURE_FLAG_NAME } from "common/constants";
import { formatCoordinates } from "common/utils/coordinates";
import { track } from "../../utils/tracking";
import {
  ObjectInfoContainer,
  ObjectInfoRow,
  PrivatedInfo,
} from "../AddressPanel/Header/__styles__/Header";
import { CoordinatesContainer } from "../AddressPanel/Header/__styles__/Header";
import { AuthContext } from "../Authorization/AuthContext";
import { ActionButton } from "../Common/Buttons/ActionButton";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body, Title } from "../Common/Typography";
import { LayerContext } from "../Maps/layers";
import { ButtonRow, HeaderContainer } from "./__styles__/Header";
import { ShareCustomObjectButton } from "./ShareCustomObjectButton";
import { useShareCustomObjectModal } from "./ShareCustomObjectModal";

type ObjectInfoHeaderProps = {
  loading: boolean;
  objectType: string;
  objectName: string;
  customMapId: string;
  geometryId: string;
  hiddenFromPublic: boolean;
  onClose: () => void;
};

const LOADING_TEXT = "Loading...";

const Header = ({
  loading,
  objectType,
  objectName,
  customMapId,
  geometryId,
  hiddenFromPublic,
  onClose,
}: ObjectInfoHeaderProps) => {
  const { markerPoint } = useContext(LayerContext);
  const { account, isFeatureEnabled } = useContext(AuthContext);
  const longitude = markerPoint?.longitude ?? null;
  const latitude = markerPoint?.latitude ?? null;
  const displayCoordinates = longitude && latitude;

  const objectInfoRef = useRef<HTMLDivElement>(null);
  const coordinatesRef = useRef<HTMLDivElement>(null);
  const [coordinatesWrapped, setCoordinatesWrapped] = useState(false);

  const showShareButton =
    account?.publicPortal.enabled &&
    isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS);

  const [showShareModal] = useShareCustomObjectModal({
    customMapId,
    geometryId,
    objectName,
    objectType,
  });

  useEffect(() => {
    const checkWrap = () => {
      if (objectInfoRef.current && coordinatesRef.current) {
        setCoordinatesWrapped(
          objectInfoRef.current.offsetTop !== coordinatesRef.current.offsetTop,
        );
      }
    };

    checkWrap();
    window.addEventListener("resize", checkWrap);
    return () => window.removeEventListener("resize", checkWrap);
  }, [coordinatesRef.current, objectInfoRef.current]);

  return (
    <HeaderContainer data-panel-header>
      {loading ? (
        <div>
          <em>{LOADING_TEXT}.</em>
        </div>
      ) : (
        <>
          <ObjectInfoRow>
            <ObjectInfoContainer ref={objectInfoRef}>
              <Body
                size="small"
                type="regular"
                color="contentSecondaryInverted"
              >
                {objectType}
              </Body>
              <CoordinatesContainer ref={coordinatesRef} displayCoordinates>
                {displayCoordinates && (
                  <Body
                    size="small"
                    type="regular"
                    color="contentSecondaryInverted"
                  >
                    {!coordinatesWrapped && `• `}
                    {`Coordinates: ${formatCoordinates({
                      latitude,
                      longitude,
                    })}`}
                  </Body>
                )}
              </CoordinatesContainer>
            </ObjectInfoContainer>
            <ActionButton
              icon="x"
              onClick={onClose}
              aria-label="Close Object Information Panel"
              data-panel-close
              variant="inverted"
            />
          </ObjectInfoRow>
          <Title size="default" type="semiBold" color="contentPrimaryInverted">
            {objectName}
          </Title>
          {showShareButton && (
            <ButtonRow>
              <ShareCustomObjectButton
                disabled={hiddenFromPublic}
                onPress={() => {
                  track("Share custom object button clicked", {
                    geometryLabel: objectName,
                    customMapName: objectType,
                  });
                  showShareModal();
                }}
              />
            </ButtonRow>
          )}
          {hiddenFromPublic && showShareButton && (
            <PrivatedInfo>
              <Icon
                iconName="eye-off"
                color="contentSecondaryInverted"
                size={16}
              />
              <div>Object hidden from public</div>
            </PrivatedInfo>
          )}
        </>
      )}
    </HeaderContainer>
  );
};

export default Header;
